<template>
  <div class="user-avatar" :style="{ width: size + 'px', height: size + 'px', borderRadius: rounded ? '50%' : '6px' }">
    <img
        v-if="src && !imgError"
        :src="src"
        :alt="name"
        :style="{ width: size + 'px', height: size + 'px', borderRadius: 'inherit', objectFit: 'cover' }"
        @error="imgError = true"
    />
    <div v-else class="identicon" :style="identiconStyle" v-html="identiconSvg" />
  </div>
</template>

<script>
/** Deterministic pixel identicon — no external deps, no network call. */
function generateIdenticon(seed, size) {
  let hash = 0
  const s = String(seed || '?')
  for (let i = 0; i < s.length; i++) {
    hash = Math.imul(31, hash) + s.charCodeAt(i) | 0
  }

  const hue  = Math.abs(hash) % 360
  const sat  = 45 + (Math.abs(hash >> 8)  % 25)  // 45–70%
  const lit  = 48 + (Math.abs(hash >> 16) % 14)  // 48–62%
  const fg   = `hsl(${hue},${sat}%,${lit}%)`
  const bg   = `hsl(${hue},${Math.round(sat * 0.3)}%,92%)`

  const cols = 5
  const rows = 5
  const cell = size / cols

  // Fill 3 columns (mirrored → 5), 5 rows → 15 bits from hash
  const rects = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < 3; c++) {
      const bit = (hash >> (r * 3 + c)) & 1
      if (!bit) continue
      const x1 = c * cell
      const x2 = (cols - 1 - c) * cell
      rects.push(`<rect x="${x1}" y="${r * cell}" width="${cell}" height="${cell}" fill="${fg}"/>`)
      if (x1 !== x2) {
        rects.push(`<rect x="${x2}" y="${r * cell}" width="${cell}" height="${cell}" fill="${fg}"/>`)
      }
    }
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" fill="${bg}"/>${rects.join('')}</svg>`
}

export default {
  name: 'UserAvatar',

  props: {
    src:     { type: String,  default: null },
    name:    { type: String,  default: '' },
    seed:    { type: [String, Number], default: null },  // fallback id/name for identicon
    size:    { type: Number,  default: 32 },
    rounded: { type: Boolean, default: true }
  },

  data() {
    return { imgError: false }
  },

  watch: {
    src() { this.imgError = false }
  },

  computed: {
    identiconSeed() {
      return this.seed ?? this.name ?? '?'
    },
    identiconSvg() {
      return generateIdenticon(this.identiconSeed, this.size)
    },
    identiconStyle() {
      return {
        width:        this.size + 'px',
        height:       this.size + 'px',
        borderRadius: this.rounded ? '50%' : '6px',
        overflow:     'hidden',
        flexShrink:   0
      }
    }
  }
}
</script>

<style scoped>
.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.identicon {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.identicon :deep(svg) {
  display: block;
}
</style>
